/**
 * Client-Side YouTube Video Extractor
 * 
 * This service provides a hybrid approach for YouTube video extraction
 * when server-side methods are blocked by bot detection. It makes requests
 * to our own server with enhanced client parameters that can help bypass
 * detection by simulating a browser-initiated request.
 */

const logger = {
  info: (_message: string) => {}, // console.log(`[ClientYouTubeExtractor] ${_message}`),
  error: (_message: string) => {
    // console.error(`[ClientYouTubeExtractor] ${message}`); // Commented out for production
  },
  warning: (_message: string) => {
    // console.warn(`[ClientYouTubeExtractor] ${message}`); // Commented out for production
  }
};

interface _VideoFormat {
  itag: number;
  url: string;
  mimeType: string;
  quality: string;
  qualityLabel?: string;
  width?: number;
  height?: number;
  fps?: number;
  bitrate?: number;
  contentLength?: string;
  approxDurationMs?: string;
}

interface VideoInfo {
  title: string;
  duration: number;
  thumbnail: string;
  videoBlob: Blob;
  filename: string;
}

class ClientYouTubeExtractor {
  private extractVideoId(url: string): string {
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
      /youtube\.com\/v\/([a-zA-Z0-9_-]{11})/,
      /youtube\.com\/watch\?.*v=([a-zA-Z0-9_-]{11})/
    ];
    
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) return match[1];
    }
    
    throw new Error('Invalid YouTube URL');
  }



  public async extractVideo(url: string, onProgress?: (progress: number) => void): Promise<VideoInfo> {
    try {
      // Step 1: Extract video ID
      const videoId = this.extractVideoId(url);
      
      if (onProgress) onProgress(10);
      
      // Step 2: Get video metadata and stream URLs using YouTube's public API
      const videoInfo = await this.getVideoMetadata(videoId);
      
      if (onProgress) onProgress(25);
      
      // Step 3: Get the best available stream URL
      const streamUrl = await this.getBestStreamUrl(videoId, videoInfo);
      
      if (onProgress) onProgress(40);
      
      // Step 4: Download the video as a blob in the browser
      const videoBlob = await this.downloadVideoBlob(streamUrl, onProgress);
      
      if (onProgress) onProgress(95);
      
      // Step 5: Create filename using only the video title (no video ID)
      const sanitizedTitle = videoInfo.title
        .replace(/[^\w\s\-.]/g, '') // Keep alphanumeric, spaces, hyphens, and dots
        .replace(/\s+/g, ' ') // Replace multiple spaces with single space
        .trim();
      const filename = `${sanitizedTitle.substring(0, 100)}.mp4`;
      
      if (onProgress) onProgress(100);
      
      return {
        title: videoInfo.title,
        duration: videoInfo.duration,
        thumbnail: videoInfo.thumbnail,
        videoBlob: videoBlob,
        filename: filename
      };
      
    } catch (error) {
      throw new Error(
        `Local download failed: ${error instanceof Error ? error.message : 'Unknown error'}. ` +
        'This video may be private, restricted, or have download protection enabled.'
      );
    }
  }

  private async getVideoMetadata(videoId: string): Promise<any> {
    // Use YouTube's oEmbed API (no auth required) for basic metadata
    const oembedUrl = `https://www.youtube.com/oembed?url=https://www.youtube.com/watch?v=${videoId}&format=json`;
    
    try {
      const response = await fetch(oembedUrl);
      if (!response.ok) throw new Error('Video not found or private');
      
      const data = await response.json();
      
      return {
        title: data.title,
        duration: 0, // oEmbed doesn't provide duration, we'll estimate
        thumbnail: data.thumbnail_url
      };
    } catch (error) {
      throw new Error('Unable to fetch video metadata. Video may be private or restricted.');
    }
  }

  private async getBestStreamUrl(videoId: string, _videoInfo: any): Promise<string> {
    // Skip server-side method and go straight to client-side approaches
    logger.info('🔄 Using pure client-side extraction methods');
    
    // Method 2: Use a CORS proxy to get stream URLs (last resort)
    try {
      logger.info('🔄 Attempting CORS proxy method');
      
      // Use a public CORS proxy (note: in production, you'd want your own proxy)
      const proxyUrl = 'https://api.allorigins.win/raw?url=';
      const youtubeUrl = `https://www.youtube.com/watch?v=${videoId}`;
      const proxiedUrl = proxyUrl + encodeURIComponent(youtubeUrl);
      
      const response = await fetch(proxiedUrl);
      const html = await response.text();
      
      // Extract stream URLs from the YouTube page HTML
      const streamUrls = this.extractStreamUrlsFromHtml(html);
      
      if (streamUrls.length > 0) {
        logger.info('✅ CORS proxy method succeeded');
        return streamUrls[0];
      }
      
      throw new Error('No stream URLs found in page content');
      
    } catch (error) {
      logger.info('🔄 CORS proxy method failed');
    }
    
    // All methods failed - provide comprehensive error
    throw new Error(
      'Unable to extract video stream URLs. This requires either:\n' +
      '1. A dedicated YouTube extraction service\n' +
      '2. Browser extension with special permissions\n' +
      '3. Server-side processing (which we already tried)\n\n' +
      'Client-side YouTube downloading is technically complex due to YouTube\'s protection measures.'
    );
  }

  private extractStreamUrlsFromHtml(html: string): string[] {
    const streamUrls: string[] = [];
    
    try {
      // Look for stream URLs in the HTML
      // This is a simplified regex - in practice, YouTube's format changes frequently
      const urlRegex = /"url":"([^"]*)"/g;
      let match;
      
      while ((match = urlRegex.exec(html)) !== null) {
        const url = match[1].replace(/\\u0026/g, '&').replace(/\\/g, '');
        if (url.includes('googlevideo.com') && url.includes('mime=video')) {
          streamUrls.push(url);
        }
      }
      
      // Sort by quality (this is very simplified)
      return streamUrls.sort((a, b) => {
        const qualityA = this.extractQuality(a);
        const qualityB = this.extractQuality(b);
        return qualityB - qualityA; // Higher quality first
      });
      
    } catch (error) {
      // console.error('Error parsing stream URLs:', error);
      return [];
    }
  }

  private extractQuality(url: string): number {
    // Extract quality indicator from URL
    if (url.includes('quality=hd720')) return 720;
    if (url.includes('quality=hd1080')) return 1080;
    if (url.includes('quality=medium')) return 480;
    if (url.includes('quality=small')) return 240;
    return 360; // default
  }

  private async downloadVideoBlob(streamUrl: string, onProgress?: (progress: number) => void): Promise<Blob> {
    const response = await fetch(streamUrl);
    
    if (!response.ok) {
      throw new Error('Failed to download video stream');
    }
    
    const contentLength = response.headers.get('content-length');
    const total = parseInt(contentLength || '0', 10);
    
    let loaded = 0;
    const chunks: BlobPart[] = [];
    
    const reader = response.body?.getReader();
    if (!reader) throw new Error('Unable to read video stream');
    
    while (true) {
      const { done, value } = await reader.read();
      
      if (done) break;
      
      chunks.push(value);
      loaded += value.length;
      
      if (onProgress && total > 0) {
        const progress = 40 + (loaded / total) * 50; // 40-90% range for download
        onProgress(Math.min(progress, 90));
      }
    }
    
    return new Blob(chunks, { type: 'video/mp4' });
  }
}

// Export singleton instance
const clientYouTubeExtractor = new ClientYouTubeExtractor();
export default clientYouTubeExtractor;